/**
 * Parses LLM response for task objectives and converts it to the form structure
 * expected by TaskObjectivesEditor.
 *
 * @param {Object} response - The LLM response object
 * @returns {Object} Parsed data in the format expected by handleMessage function
 *
 * @example
 * // Input:
 * {
 *   "objectives": [
 *     {
 *       "dimension": "content",
 *       "objectives": ["Understand basic concepts", "Apply knowledge"]
 *     }
 *   ]
 * }
 *
 * // Output:
 * {
 *   "dimensions": {
 *     "content": ["Understand basic concepts", "Apply knowledge"]
 *   }
 * }
 */

interface ObjectiveItem {
  dimension: string;
  objectives: string[];
}

interface LLmResponse {
  objectives: ObjectiveItem[];
}

interface ParsedResponse {
  dimensions: Record<string, string[]>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const parseLLmResponse = (response: any): ParsedResponse => {
  // Validate input - silently handle invalid data during streaming
  if (!response || typeof response !== 'object') {
    return { dimensions: {} };
  }

  const llmResponse = response as LLmResponse;

  if (!Array.isArray(llmResponse.objectives)) {
    return { dimensions: {} };
  }

  // Convert objectives array to dimensions object
  const dimensions: Record<string, string[]> = {};

  llmResponse.objectives.forEach((objectiveItem) => {
    // Validate each objective item - silently skip invalid items during streaming
    if (!objectiveItem || typeof objectiveItem !== 'object') {
      return;
    }

    const { dimension, objectives } = objectiveItem;

    // Validate dimension key - silently skip if missing/invalid during streaming
    if (!dimension || typeof dimension !== 'string' || dimension.trim() === '') {
      return;
    }

    // Validate objectives array - silently skip if missing/invalid during streaming
    if (!Array.isArray(objectives)) {
      return;
    }

    // Filter out invalid objective strings
    const validObjectives = objectives.filter((obj) => typeof obj === 'string' && obj.trim() !== '');

    // Only add dimension if it has valid objectives
    if (validObjectives.length > 0) {
      dimensions[dimension] = validObjectives;
    }
  });

  return { dimensions };
};

export default parseLLmResponse;
